import { useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import type { AllDayTripNote, DayView } from "@poi-planner/shared";
import {
  useCalendarDndStore,
  type DateRangePreview,
} from "../../store/calendarDndStore";
import { useTripStore } from "../../store/tripStore";
import { useUIStore } from "../../store/uiStore";
import {
  resizeDateRange,
  type RangeEdge,
} from "../../utils/calendarRangeResize";
import { daysBetweenDates } from "../../utils/zonedTime";
import { dndIds } from "../../utils/dndIds";
import { Button } from "../Button";
import type { BoardDragData, CalendarDateDropData } from "./dragTypes";
import {
  DateRangeResizeHandle,
  type DateRangeResizeTarget,
} from "./DateRangeResizeHandle";

function WeekDateDropTarget({ date, zone }: { date: string; zone: string }) {
  const preview = useCalendarDndStore((state) => state.dateRangePreview);
  const inPreview = Boolean(
    preview && date >= preview.startDate && date <= preview.endDate,
  );
  const isTarget = preview?.targetDate === date;
  const { setNodeRef, isOver } = useDroppable({
    id: dndIds.calendarDate(date),
    data: { type: "calendar-date", date, zone } satisfies CalendarDateDropData,
  });
  return (
    <div
      ref={setNodeRef}
      className={clsx(
        "min-w-0 rounded-md transition-colors",
        isOver && "bg-calendar-all-day-bg",
        inPreview &&
          (preview?.invalid ? "bg-error-bg-strong" : "bg-calendar-all-day-bg"),
        isTarget && "ring-2 ring-inset ring-info",
      )}
      data-calendar-date-target={date}
      data-calendar-zone={zone}
      data-week-all-day-target
    />
  );
}

function WeekAllDayRange({
  note,
  row,
  startColumn,
  endColumn,
  dateCount,
  zoneForDate,
  isReadOnly,
}: {
  note: AllDayTripNote;
  row: number;
  startColumn: number;
  endColumn: number;
  dateCount: number;
  zoneForDate: (date: string) => string;
  isReadOnly: boolean;
}) {
  const trip = useTripStore((state) => state.trip);
  const updateNote = useTripStore((state) => state.updateNote);
  const openNoteEditor = useUIStore((state) => state.openNoteEditor);
  const canChange = !isReadOnly && !note.locked;
  const dragData = useMemo<Extract<BoardDragData, { type: "all-day-note" }>>(
    () => ({
      type: "all-day-note",
      noteId: note.id,
      startDate: note.allDayStartDate,
      endDate: note.allDayEndDate,
      grabbedIndex: 0,
      dateCount,
      label: note.name,
    }),
    [dateCount, note],
  );
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dndIds.weekAllDayNote(note.id),
    data: dragData,
    disabled: !canChange,
  });
  const wasDragging = useRef(false);
  useEffect(() => {
    if (isDragging) {
      wasDragging.current = true;
      return;
    }
    if (!wasDragging.current) return;
    const reset = window.setTimeout(() => {
      wasDragging.current = false;
    }, 0);
    return () => window.clearTimeout(reset);
  }, [isDragging]);

  const previewForEdge = (
    edge: RangeEdge,
    target: DateRangeResizeTarget,
  ): DateRangePreview => {
    const resized = trip
      ? resizeDateRange(
          note.allDayStartDate,
          note.allDayEndDate,
          edge,
          target.date,
          trip.startDate,
          trip.endDate,
        )
      : null;
    return {
      startDate: resized?.startDate ?? target.date,
      endDate: resized?.endDate ?? target.date,
      targetDate: target.date,
      label: `${note.name} ${edge}`,
      invalid: !resized,
      conflict: false,
    };
  };
  const commitEdge = (edge: RangeEdge, target: DateRangeResizeTarget) => {
    if (!trip) return;
    const resized = resizeDateRange(
      note.allDayStartDate,
      note.allDayEndDate,
      edge,
      target.date,
      trip.startDate,
      trip.endDate,
    );
    if (
      !resized ||
      (resized.startDate === note.allDayStartDate &&
        resized.endDate === note.allDayEndDate)
    ) {
      return;
    }
    updateNote(note.id, {
      allDayStartDate: resized.startDate,
      allDayEndDate: resized.endDate,
    });
  };

  return (
    <div
      className="group/range pointer-events-auto relative z-10 min-w-0"
      style={{
        gridColumn: `${startColumn} / ${endColumn}`,
        gridRow: row,
      }}
      onPointerDownCapture={(event) => {
        if (!canChange) return;
        const target = document
          .elementsFromPoint(event.clientX, event.clientY)
          .find((element) =>
            (element as HTMLElement).hasAttribute?.("data-week-all-day-target"),
          ) as HTMLElement | undefined;
        const targetDate = target?.dataset.calendarDateTarget;
        if (targetDate) {
          dragData.grabbedIndex = daysBetweenDates(
            note.allDayStartDate,
            targetDate,
          );
        }
      }}
    >
      <Button
        ref={setNodeRef}
        variant="unstyled"
        {...(canChange ? attributes : {})}
        {...(canChange ? listeners : {})}
        disabled={isReadOnly}
        onClick={(event) => {
          event.stopPropagation();
          if (wasDragging.current) {
            wasDragging.current = false;
            return;
          }
          openNoteEditor("edit", note.id);
        }}
        className={clsx(
          "flex h-7 w-full min-w-0 items-center gap-1.5 rounded-md bg-calendar-all-day-bg-strong px-2.5 text-left text-xs font-extrabold text-calendar-all-day-text shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-calendar-all-day-border",
          canChange
            ? "cursor-grab touch-none hover:brightness-95 active:cursor-grabbing"
            : "cursor-pointer",
          isDragging && "opacity-30",
        )}
        title={
          canChange
            ? `Drag to move ${note.name}; use the edge handles to change its dates`
            : `${note.name} · ${note.allDayStartDate} – ${note.allDayEndDate}`
        }
        aria-label={`Open all-day note ${note.name}`}
        data-testid={`week-all-day-note-${note.id}`}
      >
        <span
          className="material-symbols-outlined shrink-0 text-sm"
          aria-hidden
        >
          calendar_today
        </span>
        <span className="min-w-0 flex-1 truncate">{note.name}</span>
        {dateCount > 1 && (
          <span className="shrink-0 text-2xs opacity-70">{dateCount}d</span>
        )}
        {note.locked && (
          <span
            className="material-symbols-outlined shrink-0 text-xs"
            aria-label="Locked"
          >
            lock
          </span>
        )}
      </Button>
      {canChange && (
        <>
          <DateRangeResizeHandle
            edge="start"
            label={note.name}
            currentDate={note.allDayStartDate}
            zoneForDate={zoneForDate}
            previewForTarget={(target) => previewForEdge("start", target)}
            commitTarget={(target) => commitEdge("start", target)}
          />
          <DateRangeResizeHandle
            edge="end"
            label={note.name}
            currentDate={note.allDayEndDate}
            zoneForDate={zoneForDate}
            previewForTarget={(target) => previewForEdge("end", target)}
            commitTarget={(target) => commitEdge("end", target)}
          />
        </>
      )}
    </div>
  );
}

/**
 * Continuous all-day ranges aligned to the Week view's date columns. Renders
 * into the parent all-day grid (which must be `relative` and share the day
 * columns) so notes pack into the same lanes as legs.
 */
export function WeekAllDayRail({
  dayViews,
  notes,
  lanes,
  isReadOnly,
}: {
  dayViews: DayView[];
  notes: AllDayTripNote[];
  /** Zero-based lane per note id. */
  lanes: Map<string, number>;
  isReadOnly: boolean;
}) {
  const dateIndex = useMemo(
    () => new Map(dayViews.map((day, index) => [day.date, index])),
    [dayViews],
  );
  const zoneByDate = useMemo(
    () => new Map(dayViews.map((day) => [day.date, day.zone])),
    [dayViews],
  );
  const zoneForDate = (date: string) =>
    zoneByDate.get(date) ?? dayViews[0]?.zone ?? "UTC";

  if (notes.length === 0) return null;

  return (
    <>
      <div
        className="pointer-events-auto absolute inset-0 grid gap-x-2.5"
        style={{
          gridTemplateColumns: `repeat(${dayViews.length}, minmax(0, 1fr))`,
        }}
        data-testid="week-all-day-rail"
      >
        {dayViews.map((day) => (
          <WeekDateDropTarget key={day.date} date={day.date} zone={day.zone} />
        ))}
      </div>
      {notes.map((note) => {
        const startIndex = dateIndex.get(note.allDayStartDate);
        const endIndex = dateIndex.get(note.allDayEndDate);
        if (startIndex == null || endIndex == null) return null;
        return (
          <WeekAllDayRange
            key={note.id}
            note={note}
            row={(lanes.get(note.id) ?? 0) + 1}
            startColumn={startIndex + 1}
            endColumn={endIndex + 2}
            dateCount={endIndex - startIndex + 1}
            zoneForDate={zoneForDate}
            isReadOnly={isReadOnly}
          />
        );
      })}
    </>
  );
}
